import type { ReactNode } from "react";
import type React from "react";
import { memo, useMemo } from "react";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PageHeader } from "../../../components/primitives/layout/PageHeader";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../components/primitives/data-display/DataSequence";
import type { ListPagination } from "../../../lib/pagination";
import { BatchJobKindBadge } from "../../../components/domain/data-display/BatchJobKindBadge";
import { BatchJobStatusBadge } from "../../../components/domain/data-display/BatchJobStatusBadge";
import type { BatchJob } from "../../../lib/corpusApi";
import { BATCH_JOB_KIND_VIEWS } from "./batch_job_kinds";

/** What the list covers, so a curator knows why an older job is missing. */
const LIST_DESCRIPTION =
  "Corpus batch jobs and their progress. Only jobs created in the last month are listed.";

function getJobRowId(job: BatchJob): string {
  return job.id;
}

function doneCount(job: BatchJob): number {
  return job.itemCount - job.openItemCount;
}

function progressValue(job: BatchJob): number {
  return job.itemCount === 0 ? 0 : (doneCount(job) / job.itemCount) * 100;
}

// An active job can't be deleted — it would vanish from view while still running.
const ACTIVE_STATUSES = new Set<BatchJob["status"]>(["queued", "running", "paused"]);

const baseColumns: DataSequenceColumn<BatchJob>[] = [
  {
    key: "name",
    header: "Name",
    cardRole: "primary",
    cell: (job) => <Typography variant="body2">{job.name}</Typography>,
  },
  {
    key: "kind",
    header: "Kind",
    cell: (job) => (
      <BatchJobKindBadge
        label={BATCH_JOB_KIND_VIEWS[job.kind].label}
        feedManaged={Boolean(job.corpusFeedId)}
      />
    ),
  },
  {
    key: "status",
    header: "Status",
    cell: (job) => <BatchJobStatusBadge status={job.status} />,
  },
  {
    key: "progress",
    header: "Progress",
    cell: (job) => (
      <Stack gap={0.5} sx={{ minWidth: 120 }}>
        <LinearProgress variant="determinate" value={progressValue(job)} />
        <Typography variant="caption" color="text.secondary">
          {doneCount(job)}/{job.itemCount}
        </Typography>
      </Stack>
    ),
  },
  {
    key: "openItemCount",
    header: "Remaining",
    cell: (job) => (job.openItemCount > 0 ? job.openItemCount : "—"),
  },
  {
    key: "failedItemCount",
    header: "Errors",
    cell: (job) =>
      job.failedItemCount > 0 ? (
        <Typography variant="body2" color="error">
          {job.failedItemCount}
        </Typography>
      ) : (
        "—"
      ),
  },
  {
    key: "createdBy",
    header: "Created by",
    cell: (job) => (
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ maxWidth: 160, display: "block" }}
      >
        {job.createdBy}
      </Typography>
    ),
  },
  {
    key: "createdAt",
    header: "Created",
    cell: (job) => (
      <Typography variant="caption" color="text.secondary">
        {new Date(job.createdAt).toLocaleString()}
      </Typography>
    ),
  },
  {
    key: "finishedAt",
    header: "Finished",
    cell: (job) => (
      <Typography variant="caption" color="text.secondary">
        {job.finishedAt ? new Date(job.finishedAt).toLocaleString() : "—"}
      </Typography>
    ),
  },
];

function deleteColumn(onDelete: (job: BatchJob) => void): DataSequenceColumn<BatchJob> {
  return {
    key: "delete",
    header: "",
    align: "right",
    cardRole: "action",
    cell: (job) =>
      ACTIVE_STATUSES.has(job.status) ? null : (
        <Tooltip title="Archive">
          <IconButton size="small" aria-label="Archive" onClick={() => onDelete(job)}>
            <ArchiveOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
  };
}

export interface BatchJobsBodyProps {
  jobs: BatchJob[];
  loading: boolean;
  error: string | null;
  pagination: ListPagination;
  onRowClick: (job: BatchJob) => void;
  /** Deletes a job (shown to the person as archiving). Left out for a viewer who may not do it,
   *  which hides the action column. */
  onDelete?: (job: BatchJob) => void;
  /** Slot for the role-gated "New job" button — kept out of this presentational component so it
   *  stays independent of auth context. */
  newJobAction?: ReactNode;
}

/** Presentational batch jobs list: header, progress table, and pagination for already-loaded
 *  jobs. */
export const BatchJobsBody = memo(function BatchJobsBody({
  jobs,
  loading,
  error,
  pagination,
  onRowClick,
  onDelete,
  newJobAction,
}: BatchJobsBodyProps) {
  const columns = useMemo(
    () => (onDelete ? [...baseColumns, deleteColumn(onDelete)] : baseColumns),
    [onDelete]
  );

  return (
    <Stack gap={2}>
      <PageHeader title="Batch jobs" subtitle={LIST_DESCRIPTION} actions={newJobAction} />

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={jobs}
          getRowId={getJobRowId}
          emptyMessage="No batch jobs in the last month."
          onRowClick={onRowClick}
          pagination={pagination}
        />
      )}
    </Stack>
  );
});
